// DatabaseSettingsMissionTemplateRow.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './DatabaseSettingsMissionTemplateRow.css';

// Icons
import Icon from 'svg/Icon';

/**
 * DatabaseSettingsMissionTemplateRow
 *
 * Renders one draggable Mission template row in the database settings Missions tab.
 *
 * @param {boolean} isDragging - Whether the row is currently being dragged.
 * @param {boolean} isDropTarget - Whether the row is the active drop target.
 * @param {string} label - Primary Mission title.
 * @param {string} note - Secondary Mission summary.
 * @param {function} onClick - Opens the workflow designer.
 * @param {function} onDragEnd - Clears drag state.
 * @param {function} onDragOver - Handles drag-over events.
 * @param {function} onDragStart - Starts dragging.
 * @param {function} onDrop - Handles drop.
 * @param {function} onSettingsClick - Opens the workflow designer.
 *
 * @returns {ReactNode} The rendered Mission template row.
 */
const DatabaseSettingsMissionTemplateRow = React.memo(({
  iconKey = 'account_tree',
  isDragging,
  isDropTarget,
  label,
  note,
  onClick,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onSettingsClick,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- RENDER ----- //

  return (
    <div
      className={`database-settings-mission-template-row-container${isDragging ? ' dragging' : ''}`}
      style={{
        backgroundColor: isDropTarget ? theme.backgroundHover : theme.panel,
        borderColor: isDropTarget ? theme.foregroundMuted : theme.backgroundHover,
        cursor: 'move',
      }}
      draggable
      title={label || 'Playbook'}
      onClick={onClick}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
      onDrop={onDrop}
    >

      {/* MAIN */}
      <div className="database-settings-mission-template-row-main">

        {/* ICON */}
        <div className="database-settings-mission-template-row-icon">
          <Icon
            color={theme.foregroundMuted}
            height={18}
            iconKey={iconKey || 'account_tree'}
            width={18}
          />
        </div>

        {/* COPY */}
        <div className="database-settings-mission-template-row-copy">

          {/* LABEL */}
          <div
            className="database-settings-mission-template-row-label"
          >
            {label}
          </div>

          {/* NOTE */}
          {note ? (
            <div
              className="database-settings-mission-template-row-note"
            >
              {note}
            </div>
          ) : null}
        </div>
      </div>

      {/* ACTIONS */}
      <div className="database-settings-mission-template-row-actions">

        {/* SETTINGS → DESIGNER */}
        <button
          className="database-settings-mission-template-row-action"
          title="Design playbook"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSettingsClick?.();
          }}
        >
          <Icon
            color={theme.foregroundMuted}
            height={16}
            iconKey="settings"
            width={16}
          />
        </button>

        {/* DRAG HANDLE */}
        <div
          className="database-settings-mission-template-row-drag-handle"
          title="Drag to reorder"
        >
          <Icon
            color={theme.foregroundMuted}
            height={18}
            iconKey="drag_indicator"
            width={18}
          />
        </div>
      </div>
    </div>
  );
});

// Dev Tools
DatabaseSettingsMissionTemplateRow.displayName = 'DatabaseSettingsMissionTemplateRow';

export default DatabaseSettingsMissionTemplateRow;
